/**
 * Firm Knowledge API — workspace-level persistent context for Archie agents.
 * GET/PATCH /api/workspaces/:id/firm-knowledge
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface TeamContact {
  name: string;
  role: string;
}

export interface FirmProfile {
  practice_name?: string;
  legal_name?: string;
  jurisdiction?: 'AU' | 'US' | 'UK' | 'NZ' | 'CA';
  team_contacts?: TeamContact[];
}

export interface FirmIntegrations {
  sharepoint?: { site_url?: string; drive_path?: string };
  ato?: { agency_number?: string; rep_type?: string };
  xero?: { org_id?: string };
  custom?: Array<{ key: string; value: string }>;
}

export interface FirmBrowser {
  proxy_country?: string;
  default_timeout_ms?: number;
  max_retries?: number;
}

export interface FirmKnowledge {
  profile?: FirmProfile;
  credentials?: Record<string, string>; // vault key names only — never actual secrets
  integrations?: FirmIntegrations;
  policies?: string[];
  browser?: FirmBrowser;
  updated_at?: string;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const token = localStorage.getItem('martha_token');
  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export function useFirmKnowledge(workspaceId: string) {
  return useQuery({
    queryKey: ['firm-knowledge', workspaceId],
    queryFn: () => request<{ data: FirmKnowledge }>('GET', `/api/workspaces/${workspaceId}/firm-knowledge`),
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

export function useUpdateFirmKnowledge(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<FirmKnowledge>) =>
      request<{ data: FirmKnowledge }>('PATCH', `/api/workspaces/${workspaceId}/firm-knowledge`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['firm-knowledge', workspaceId] });
    },
  });
}
